import React from 'react';

import { Select } from '@aline/common';
import type { useSortable } from '@dnd-kit/sortable';
import { IconButton } from '@mui/material';
import { GripVertical, Trash2 } from 'lucide-react';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<
  { dragging: boolean; overlay: boolean; selected: boolean },
  'box' | 'deleteButton' | 'dragHandleIcon'
>()((theme, { dragging, overlay, selected }, classes) => ({
  // The tinted name row. Hover is driven from the wrapper so reaching for the
  // grip in the card padding still highlights the row it belongs to.
  box: {
    alignItems: 'center',
    backgroundColor: selected ? theme.appColors.selectedBackground : 'transparent',
    borderRadius: 6,
    boxSizing: 'border-box',
    display: 'flex',
    padding: theme.spacing(0, 1),
    width: '100%',
  },
  // The floating copy under the pointer: lifted off the page, and opaque so
  // the rows it passes over do not show through.
  boxOverlay: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
  },
  /**
   * Both controls are revealed on row hover so each row reads as a name rather
   * than a name between two icons. Keyboard focus reveals them too, but via
   * :focus-visible rather than :focus-within — clicking the label focuses it,
   * and the plain focus would otherwise pin the icons on the selected row.
   */
  deleteButton: {
    color: theme.appColors.secondaryType,
    flexShrink: 0,
    opacity: 0,
    transition: 'opacity 120ms ease',
  },
  /**
   * Sits in the category card's left padding, outside the tinted row but still
   * inside the card. The gutter is the card's side padding (theme.spacing(2)).
   */
  dragHandle: {
    alignItems: 'center',
    bottom: 0,
    color: theme.appColors.secondaryType,
    cursor: dragging || overlay ? 'grabbing' : 'grab',
    display: 'flex',
    justifyContent: 'center',
    left: `calc(-1 * ${theme.spacing(2)})`,
    position: 'absolute',
    top: 0,
    touchAction: 'none',
    width: theme.spacing(2),
  },
  // Only the glyph fades, so the hit area stays in place at rest.
  dragHandleIcon: {
    opacity: overlay ? 1 : 0,
    transition: 'opacity 120ms ease',
  },
  /**
   * A button rather than a field: the rule's name is edited in the details
   * panel, so the row only has to open it. Unstyled so the row around it
   * carries the selected state.
   */
  label: {
    background: 'none',
    border: 0,
    color: theme.appColors.iconsType,
    cursor: 'pointer',
    flexGrow: 1,
    font: 'inherit',
    fontSize: 14,
    minWidth: 0,
    overflow: 'hidden',
    padding: theme.spacing(0.75, 0),
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Anchors the grip in the card padding. The wrapper itself has no tint; that
  // lives on the box so the grip sits outside the selected/hover background.
  row: {
    [`&:has(.${classes.deleteButton}:focus-visible) .${classes.deleteButton}, &:hover .${classes.deleteButton}, &:has(.${classes.deleteButton}:focus-visible) .${classes.dragHandleIcon}, &:hover .${classes.dragHandleIcon}`]:
      {
        opacity: 1,
      },
    [`&:hover .${classes.box}`]: {
      backgroundColor: selected
        ? theme.appColors.selectedBackground
        : theme.appColors.hoverBackground,
    },
    // The row left behind in the list while its copy floats, so the gap
    // shows where the drop would land.
    opacity: dragging ? 0.4 : 1,
    position: 'relative',
    width: '100%',
  },
  untitled: {
    color: theme.appColors.secondaryType,
  },
}));

/** What `useSortable` hands back for the element that starts a drag. */
type DragListeners = ReturnType<typeof useSortable>['listeners'];

interface RuleRowProps {
  dataCy?: string;
  deleteDisabled?: boolean;
  /** Dims the row: it is the one being dragged, and a copy of it is floating. */
  dragging?: boolean;
  /** Listeners for the grip. Absent on the floating copy. */
  dragListeners?: DragListeners;
  /** Shown in place of the title while the rule has none. */
  fallbackLabel?: string;
  /** Absent on the floating copy, whose controls are only there to match. */
  onDelete?: () => void;
  onSelect?: () => void;
  /** Styles the row as the floating copy under the pointer. */
  overlay?: boolean;
  rootRef?: (node: HTMLElement | null) => void;
  selected?: boolean;
  style?: React.CSSProperties;
  title?: string | null;
}

export function RuleRow({
  dataCy,
  deleteDisabled = false,
  dragListeners,
  dragging = false,
  fallbackLabel = 'Untitled rule',
  onDelete,
  onSelect,
  overlay = false,
  rootRef,
  selected = false,
  style,
  title,
}: RuleRowProps): JSX.Element {
  const { classes, cx } = useStyles({ dragging, overlay, selected });
  // A title of nothing but spaces is a rule nobody has named, the same as an
  // empty one — the create path trims, but a row renders what it is given.
  const name = title?.trim();

  return (
    <div ref={rootRef} className={classes.row} data-cy={dataCy} style={style}>
      <span
        className={classes.dragHandle}
        data-cy={Select.playbooks.ruleDragHandle}
        {...dragListeners}
      >
        <GripVertical className={classes.dragHandleIcon} size={16} />
      </span>
      <div className={cx(classes.box, overlay && classes.boxOverlay)}>
        <button
          aria-pressed={selected}
          className={cx(classes.label, !name && classes.untitled)}
          data-cy={Select.playbooks.ruleSelect}
          type="button"
          onClick={onSelect}
        >
          {name || fallbackLabel}
        </button>
        <AlineTooltip arrow placement="top" title="Delete rule">
          <IconButton
            className={classes.deleteButton}
            data-cy={Select.playbooks.ruleDelete}
            disabled={deleteDisabled}
            size="small"
            onClick={onDelete}
          >
            <Trash2 size={16} />
          </IconButton>
        </AlineTooltip>
      </div>
    </div>
  );
}
